import {useContext, useMemo} from 'react';
import '../additional-providers-table/additional-providers-table.scss';
import {NetworkContext} from '../../lib/context';
import {observer} from 'mobx-react';
import {memo} from 'react';
import {useDisruptionAnalysisDataLoading} from './hooks/use-disruption-analysis-data-loading';
import {useProviderSelection} from '../additional-providers-table/hooks/use-provider-selection';
import {useProviderTableSort} from '../additional-providers-table/hooks/use-provider-table-sort';
import {useScrollToProvider} from '../additional-providers-table/hooks/use-scroll-to-provider';
import {DisruptionAnalysisAdditionalProvidersProps} from './types';
import {TableHeader as ExtendedTableHeader} from '../additional-providers-table/types';
import {getDisruptionAnalysisTableHeaders} from './table-headers';
import ProviderTableShell from '../additional-providers-table/provider-table-shell';

const DisruptionAnalysisAdditionalProvidersTable = memo(observer(({setSaveRosterModalOpen}: DisruptionAnalysisAdditionalProvidersProps) => {
    const networkOptimizationStore = useContext(NetworkContext);

    const {localProviders, existingProviders, existingProviderQueuedForDeletion, filters, filterProviderList} = networkOptimizationStore;

    // Use custom hooks to encapsulate business logic
    const {
        loading,
        error
    } = useDisruptionAnalysisDataLoading({networkOptimizationStore});

    const {
        selection,
        deselection,
        selectionSet,
        deselectionSet,
        existingProviderIds,
        handleCheckboxChange
    } = useProviderSelection({
        networkOptimizationStore,
        existingProviders
    });

    const {
        sortColumn,
        sortDirection,
        tableRows,
        handleSort
    } = useProviderTableSort({
        localProviders,
        existingProviders,
        filterProviderList: filterProviderList.bind(networkOptimizationStore),
        specialtyFilter: networkOptimizationStore.disruptionAnalysisSpecialtyFilter || '',
        filters,
        complianceScores: networkOptimizationStore.complianceScores
    });

    const {scrollToExistingProvider} = useScrollToProvider({existingProviderQueuedForDeletion});

    // Define table headers
    const tableHeaders: ExtendedTableHeader[] = useMemo(
        () => getDisruptionAnalysisTableHeaders(networkOptimizationStore.disruptionAnalysisSpecialtyFilter),
        [networkOptimizationStore.disruptionAnalysisSpecialtyFilter]
    );

    return (
        <ProviderTableShell
            loading={loading}
            error={error}
            tableRows={tableRows}
            tableHeaders={tableHeaders}
            // No loadMoreConfig - disruption analysis doesn't paginate
            skeletonRowCount={10}
            specialtyFilter={networkOptimizationStore.disruptionAnalysisSpecialtyFilter}
            networkOptimizationStore={networkOptimizationStore}
            setSaveRosterModalOpen={setSaveRosterModalOpen}
            selection={selection}
            deselection={deselection}
            selectionSet={selectionSet}
            deselectionSet={deselectionSet}
            existingProviderIds={existingProviderIds}
            handleCheckboxChange={handleCheckboxChange}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            scrollToExistingProvider={scrollToExistingProvider}
        />
    );
}));

DisruptionAnalysisAdditionalProvidersTable.displayName = 'DisruptionAnalysisAdditionalProvidersTable';

export default DisruptionAnalysisAdditionalProvidersTable;